import api from "../../utils/axiosConfig";
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { UserContext } from "./UserContext";

export const OperationsContext = createContext();

const API_BASE_URL = "/api/price";

export const OperationsProvider = ({ children }) => {
  const { currentUser } = useContext(UserContext);
  const userId = currentUser?.id;
  const localStorageKey = `operations_${userId}`;

  const [operations, setOperations] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const syncOperations = useCallback((fetchedOperations) => {
    setOperations(fetchedOperations);
    if (userId) {
      localStorage.setItem(localStorageKey, JSON.stringify(fetchedOperations));
    }
  }, [userId, localStorageKey]);

  const fetchOperations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { };
      if (currentUser?.role === 'moderator') {
        params.userId = currentUser.id;
        params.role = 'moderator';
      } else if (currentUser?.role) {
        params.role = currentUser.role;
      }
      const response = await api.get(API_BASE_URL, { params });
      const fetchedOperations = {};
      response.data.forEach(item => {
        // Moderator için: sadece kendi userId'si ile eşleşen fiyatları al
        // Admin/user için: sadece userId'siz (global) fiyatları al
        if (currentUser?.role === 'moderator') {
          if (item.userId === userId) {
            fetchedOperations[item.name] = {
              Bid: item.bid ? item.bid.toString() : '',
              Ask: item.ask ? item.ask.toString() : '',
            };
          }
        } else {
          // Admin ve diğer kullanıcılar için userId'siz fiyatları al
          if (!item.userId) {
            fetchedOperations[item.name] = {
              Bid: item.bid ? item.bid.toString() : '',
              Ask: item.ask ? item.ask.toString() : '',
            };
          }
        }
      });
      syncOperations(fetchedOperations);
    } catch (err) {
      setError("Fiyatlar alınırken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  }, [currentUser, userId, syncOperations]);

  // İlk yüklemede operasyonları backend ve localStorage'dan çek
  useEffect(() => {
    if (userId) {
      fetchOperations();
      // Not: Sürekli güncelleme interval'ı kaldırıldı çünkü 
      // kullanıcı fiyat güncellediğinde marjların kaybolmasına neden oluyordu
    }
  }, [userId, fetchOperations]);

  const updateOperations = async (newOperations) => {
    console.log('🔄 updateOperations başladı:', { newOperations, currentOperations: operations });
    setLoading(true);
    setError(null);
    try {
      // Tüm fiyatları topluca gönder
      const pricesArray = Object.entries(newOperations).map(([name, { Bid, Ask }]) => {
        const obj = { 
          name, 
          bid: Bid ? Bid.toString() : '', 
          ask: Ask ? Ask.toString() : '' 
        };
        if (currentUser?.role === 'moderator') {
          obj.userId = userId;
        }
        // Admin ve user için userId eklenmez
        return obj;
      });
      
      console.log('📤 API\'ye gönderilen veri:', pricesArray);
      await api.post(API_BASE_URL, pricesArray);
      console.log('✅ API başarılı');
      
      // Operations state'ini güncelle - bu PriceTable'ın otomatik güncellenmesini sağlar
      console.log('🔄 State güncelleniyor...');
      setOperations(newOperations);
      console.log('✅ State güncellendi:', newOperations);
      
      if (userId) {
        localStorage.setItem(localStorageKey, JSON.stringify(newOperations));
        console.log('💾 LocalStorage güncellendi');
      }
      
      // PriceTable'ın hemen güncellenmesi için ek state güncellemesi
      // Bu sayede fiyatlar anında görünür
      setTimeout(() => {
        console.log('🔄 100ms sonra ek state güncellemesi...');
        setOperations(prev => {
          console.log('📊 Önceki state:', prev);
          const updated = { ...prev };
          console.log('📊 Güncellenmiş state:', updated);
          return updated;
        });
      }, 100);
      
      // Ek güvenlik için bir kez daha güncelle
      setTimeout(() => {
        console.log('🔄 500ms sonra ek state güncellemesi...');
        setOperations(prev => {
          console.log('📊 Önceki state:', prev);
          const updated = { ...prev };
          console.log('📊 Güncellenmiş state:', updated);
          return updated;
        });
      }, 500);
      
    } catch (err) {
      console.error('❌ updateOperations hatası:', err);
      setError("Fiyat kaydedilirken hata oluştu.");
      throw err;
    } finally {
      setLoading(false);
      console.log('🏁 updateOperations tamamlandı');
    }
  };

  const resetOperations = () => {
    setOperations({});
    if (userId) {
      localStorage.removeItem(localStorageKey);
    }
  };

  return (
    <OperationsContext.Provider
      value={{
        operations,
        updateOperations,
        resetOperations,
        syncOperations,
        fetchOperations,
        loading,
        error,
      }}
    >
      {children}
    </OperationsContext.Provider>
  );
};

export default OperationsProvider;
